import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ExternalLink } from 'lucide-react'
import {
  ApiError,
  CreatorBusinessItem,
  DEFAULT_ORDER_BY,
  FilterOption,
  getCreatorBusinessCreators,
  getCreatorBusinessCreatorsExcel,
  getCreatorBusinessFilterOptions,
  GetCreatorBusinessParams,
  PRICE_GROUPS,
  PRICE_ITEMS,
  setLecommendToken,
} from './creatorBusiness'
import FilterBar, { FilterDimension, SortOption } from './FilterBar'
import CreatorDetailSheet from './CreatorDetailSheet'
import EstimateCalculatorModal from './EstimateCalculatorModal'
import BulkActionBar from './BulkActionBar'
import { useCreatorCart } from './useCreatorCart'

const PAGE_SIZE = 50

/** 필터 차원 (옵션 키 / 쿼리 파라미터 키 매핑) */
const DIMS = [
  { key: 'creator', label: '크리에이터', optKey: 'creators', param: 'salesforceCreatorIds', searchable: true },
  { key: 'contractType', label: '계약 형태', optKey: 'contractTypes', param: 'contractTypes' },
  { key: 'creatorLabel', label: '레이블', optKey: 'labels', param: 'creatorLabels' },
  { key: 'personalColor', label: '퍼스널 컬러', optKey: 'personalColors', param: 'personalColors' },
  { key: 'skinType', label: '피부 타입', optKey: 'skinTypes', param: 'skinTypes' },
  { key: 'skinWorry', label: '피부 고민', optKey: 'skinWorries', param: 'skinWorries' },
  { key: 'preferenceIngredient', label: '선호 성분', optKey: 'preferenceIngredients', param: 'preferenceIngredients' },
  { key: 'dislikeIngredient', label: '비선호 성분', optKey: 'dislikeIngredients', param: 'dislikeIngredients' },
  { key: 'channelScale', label: '채널 규모', optKey: 'channelScales', param: 'channelScales' },
  { key: 'businessType', label: '비즈니스 타입', optKey: 'businessTypes', param: 'businessTypes' },
  { key: 'contentsGenre', label: '콘텐츠 장르', optKey: 'contentsGenres', param: 'contentsGenres' },
  { key: 'language', label: '구사 가능 언어', optKey: 'languages', param: 'languages' },
] as const

const CONTRACT_DEFAULT: FilterOption[] = [{ id: '전속', name: '전속' }]

const CHANNEL_COLUMNS = [
  { label: 'YouTube 구독자', countKey: 'youtubeSubscriberCount', linkKey: 'youtubeLink' },
  { label: 'Instagram 팔로워', countKey: 'instagramFollowerCount', linkKey: 'instagramLink' },
] as const

const ATTR_COLUMNS: { key: keyof CreatorBusinessItem; label: string }[] = [
  { key: 'personalColor', label: '퍼스널 컬러' },
  { key: 'skinType', label: '피부 타입' },
  { key: 'skinWorry', label: '피부 고민' },
  { key: 'preferenceIngredient', label: '선호 성분' },
  { key: 'dislikeIngredient', label: '비선호 성분' },
  { key: 'channelScale', label: '채널 규모' },
  { key: 'businessType', label: '비즈니스 타입' },
  { key: 'contentsGenre', label: '콘텐츠 장르' },
  { key: 'language', label: '구사 가능 언어' },
]

const SALES_COLUMNS: { key: keyof CreatorBusinessItem; label: string }[] = [
  { key: 'contractType', label: '계약 형태' },
  { key: 'creatorLabel', label: '레이블' },
  { key: 'manager', label: '매니저' },
  { key: 'salesforceUpdatedAt', label: '업데이트 일시' },
]

const val = (c: CreatorBusinessItem, key: string): string =>
  (c[key as keyof CreatorBusinessItem] as string | undefined) || '-'

const thBase =
  'whitespace-nowrap border-b border-line-soft bg-surface-soft px-3 py-2.5 text-left lf-caption text-fg-3'
const tdBase = 'whitespace-nowrap border-b border-line-hairline px-3 py-3 text-left lf-body-sm text-fg-1'

const csv = (items: FilterOption[]) => items.map((i) => i.id).join(',')

function ChannelCell({ count, link }: { count: string; link?: string }) {
  return (
    <div className="flex items-center justify-start gap-1">
      <span className="lf-num">{count}</span>
      {link && link !== '-' && (
        <a href={link} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
          <ExternalLink size={14} className="text-fg-3 hover:text-fg-1" />
        </a>
      )}
    </div>
  )
}

export default function PlatformPage() {
  const [allItems, setAllItems] = useState<CreatorBusinessItem[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<{ message: string; status?: number } | null>(null)
  const [page, setPage] = useState(0)
  const [tokenInput, setTokenInput] = useState('')

  // 필터 / 정렬
  const [options, setOptions] = useState<Record<string, FilterOption[]>>({})
  const [selected, setSelected] = useState<Record<string, FilterOption[]>>({ contractType: CONTRACT_DEFAULT })
  const [sort, setSort] = useState<string>(DEFAULT_ORDER_BY)

  // 선택(견적 대상) / 상세 / 계산기
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [detailCreator, setDetailCreator] = useState<CreatorBusinessItem | null>(null)
  const [detailOpen, setDetailOpen] = useState(false)
  const [calcOpen, setCalcOpen] = useState(false)
  const [extracting, setExtracting] = useState(false)
  const cart = useCreatorCart()

  const scrollRef = useRef<HTMLDivElement>(null)

  // 필터 옵션 로드 (정렬 옵션 포함)
  useEffect(() => {
    getCreatorBusinessFilterOptions()
      .then((o) => {
        setOptions(o)
        const orderBys = o.orderBys ?? []
        if (orderBys.length && !orderBys.some((x) => x.id === sort)) setSort(orderBys[0].id)
      })
      .catch(() => { /* 토큰 없으면 배너로 안내 */ })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const sortOptions: SortOption[] = useMemo(
    () => (options.orderBys ?? []).map((o) => ({ value: o.id, label: o.name })),
    [options],
  )

  const params = useMemo<GetCreatorBusinessParams>(() => {
    const p: GetCreatorBusinessParams = { orderBy: sort }
    for (const d of DIMS) (p as Record<string, unknown>)[d.param] = csv(selected[d.key] ?? [])
    return p
  }, [sort, selected])

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await getCreatorBusinessCreators(params)
      setAllItems(res.items)
      setTotal(res.total)
      setPage(0)
    } catch (e) {
      const err = e as ApiError
      setError({ message: err.message, status: err.status })
      setAllItems([])
      setTotal(0)
    } finally {
      setLoading(false)
    }
  }, [params])

  useEffect(() => { void load() }, [load])

  const pageCount = Math.max(1, Math.ceil(allItems.length / PAGE_SIZE))
  const items = useMemo(
    () => allItems.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE),
    [allItems, page],
  )
  // 데이터가 줄어 현재 페이지가 범위를 벗어나면 마지막 페이지로 보정
  useEffect(() => {
    if (page > pageCount - 1) setPage(pageCount - 1)
  }, [page, pageCount])

  // 페이지 넘길 때 테이블 상단으로 스크롤
  const goPage = useCallback((p: number) => {
    setPage(Math.min(Math.max(0, p), pageCount - 1))
    scrollRef.current?.scrollTo({ top: 0 })
  }, [pageCount])

  // 필터 토글/초기화
  const makeToggle = useCallback(
    (key: string) => (opt: FilterOption) =>
      setSelected((prev) => {
        const cur = prev[key] ?? []
        const exists = cur.some((o) => o.id === opt.id)
        return { ...prev, [key]: exists ? cur.filter((o) => o.id !== opt.id) : [...cur, opt] }
      }),
    [],
  )
  const makeClear = useCallback(
    (key: string) => () =>
      setSelected((prev) => ({ ...prev, [key]: key === 'contractType' ? CONTRACT_DEFAULT : [] })),
    [],
  )

  const dimensions: FilterDimension[] = useMemo(
    () =>
      DIMS.map((d) => ({
        key: d.key,
        label: d.label,
        options: options[d.optKey] ?? [],
        selected: selected[d.key] ?? [],
        onToggle: makeToggle(d.key),
        onClear: makeClear(d.key),
        searchable: 'searchable' in d ? d.searchable : false,
      })),
    [options, selected, makeToggle, makeClear],
  )

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  const addToCart = useCallback(
    (list: CreatorBusinessItem[]) => {
      if (list.length === 0) return
      cart.addCreators(list.map((c) => ({ salesforceCreatorId: c.salesforceCreatorId, productItems: [{ key: '' }] })))
      setSelectedIds(new Set())
      setCalcOpen(true)
    },
    [cart],
  )

  const extractData = useCallback(async () => {
    setExtracting(true)
    setError(null)
    try {
      await getCreatorBusinessCreatorsExcel(params)
    } catch (e) {
      const err = e as ApiError
      setError({ message: err.message, status: err.status })
    } finally {
      setExtracting(false)
    }
  }, [params])

  // 서버 프록시가 apps 서비스 토큰을 주입 → 붙여넣기 불필요. 실제 401일 때만 폴백.
  const needsToken = error?.status === 401

  return (
    <div className="w-full px-6 py-6">
      <div className="mb-1 lf-h3 text-fg-1">크리에이터 단가 검색</div>
      <div className="mb-4 lf-body-sm text-fg-3">전속/파트너 크리에이터의 단가를 검색하고 견적을 계산할 수 있어요.</div>

      {needsToken && (
        <div className="mb-4 rounded-r-2 border border-line-soft bg-surface-soft p-3 lf-body-sm text-fg-1">
          <div className="mb-2 font-medium">lecommend 인증 토큰이 필요합니다 (bo.business.read).</div>
          <div className="flex gap-2">
            <input
              type="password"
              value={tokenInput}
              onChange={(e) => setTokenInput(e.target.value)}
              placeholder="Bearer 토큰 붙여넣기"
              className="flex-1 rounded-r-1 border border-line-soft bg-surface-base px-2 py-1 text-fg-1"
            />
            <button
              type="button"
              onClick={() => {
                if (tokenInput.trim()) {
                  setLecommendToken(tokenInput.trim())
                  setTokenInput('')
                  void load()
                }
              }}
              className="rounded-r-1 bg-brand-pink px-3 py-1 font-medium text-brand-brown hover:opacity-90"
            >
              저장 후 재조회
            </button>
          </div>
        </div>
      )}

      {error && !needsToken && (
        <div className="mb-4 rounded-r-2 border border-line-soft bg-surface-soft p-3 lf-body-sm text-brand-pink">
          {error.message}
        </div>
      )}

      <div className="mb-3">
        <FilterBar
          dimensions={dimensions}
          sort={sort}
          sortOptions={sortOptions}
          onSort={setSort}
          onOpenCalculator={() => setCalcOpen(true)}
          cartCount={cart.creators.length}
          onExtractData={extractData}
          extracting={extracting}
        />
      </div>

      <div className="mb-2 flex items-center gap-3">
        <span className="lf-body-sm text-fg-3">Total <span className="lf-num text-fg-1">{total.toLocaleString()}</span></span>
        {selectedIds.size > 0 && <span className="lf-body-sm text-brand-pink">· {selectedIds.size}명 선택됨</span>}
      </div>

      <div
        ref={scrollRef}
        className="relative w-full overflow-auto rounded-r-2 border border-line-soft max-h-[calc(100vh-300px)]"
      >
        <table className="w-full border-separate border-spacing-0">
          <thead className="[&_th]:sticky [&_th]:top-0 [&_th]:z-[2]">
            <tr>
              <th rowSpan={2} className={`${thBase} sticky left-0 z-[3] border-r border-line-soft`}>크리에이터명</th>
              <th colSpan={2} className={`${thBase} text-center`}>채널 정보</th>
              {PRICE_GROUPS.map((g) => (
                <th key={g.group} colSpan={g.items.length} className={`${thBase} text-center`}>{g.group}</th>
              ))}
              <th colSpan={ATTR_COLUMNS.length} className={`${thBase} text-center`}>크리에이터 속성</th>
              <th colSpan={SALES_COLUMNS.length} className={`${thBase} text-center`}>세일즈 포스</th>
            </tr>
            <tr className="[&_th]:top-[38px]">
              {CHANNEL_COLUMNS.map((c) => (<th key={c.countKey} className={thBase}>{c.label}</th>))}
              {PRICE_ITEMS.map((p) => (<th key={p.key} className={`${thBase} text-right`}>{p.label}</th>))}
              {ATTR_COLUMNS.map((a) => (<th key={String(a.key)} className={thBase}>{a.label}</th>))}
              {SALES_COLUMNS.map((a) => (
                <th key={String(a.key)} className={`${thBase} ${a.key === 'salesforceUpdatedAt' ? 'text-right' : ''}`}>{a.label}</th>
              ))}
            </tr>
          </thead>

          <tbody>
            {items.map((c) => (
              <tr
                key={c.salesforceCreatorId}
                onClick={() => { setDetailCreator(c); setDetailOpen(true) }}
                className="cursor-pointer hover:bg-surface-soft"
              >
                <td className={`${tdBase} sticky left-0 z-[1] w-[240px] max-w-[240px] border-r border-line-soft bg-surface-base`}>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(c.salesforceCreatorId)}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => toggleSelect(c.salesforceCreatorId)}
                    />
                    {c.creatorImageUrl && c.creatorImageUrl !== '-' ? (
                      <img src={c.creatorImageUrl} alt={c.name} className="h-7 w-7 rounded-full object-cover" />
                    ) : (
                      <span className="flex h-7 w-7 items-center justify-center rounded-full bg-surface-muted lf-caption text-fg-2">
                        {c.name?.[0] ?? '?'}
                      </span>
                    )}
                    <span className="truncate font-medium text-brand-blue underline">{c.name}</span>
                  </div>
                </td>
                {CHANNEL_COLUMNS.map((ch) => (
                  <td key={ch.countKey} className={tdBase}>
                    <ChannelCell count={val(c, ch.countKey)} link={c[ch.linkKey] as string | undefined} />
                  </td>
                ))}
                {PRICE_ITEMS.map((p) => (<td key={p.key} className={`${tdBase} text-right lf-num`}>{val(c, p.key)}</td>))}
                {ATTR_COLUMNS.map((a) => (<td key={String(a.key)} className={tdBase}>{val(c, String(a.key))}</td>))}
                {SALES_COLUMNS.map((a) => (
                  <td key={String(a.key)} className={`${tdBase} ${a.key === 'salesforceUpdatedAt' ? 'text-right' : ''}`}>{val(c, String(a.key))}</td>
                ))}
              </tr>
            ))}
            {!loading && items.length === 0 && (
              <tr>
                <td className={`${tdBase} py-12 text-center text-fg-3`} colSpan={1 + 2 + PRICE_ITEMS.length + ATTR_COLUMNS.length + SALES_COLUMNS.length}>
                  검색 결과가 없습니다.
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-surface-base/60">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-line-soft border-t-brand-pink" />
          </div>
        )}
      </div>

      {/* 페이지네이션 (API는 전체 반환 → 클라이언트에서 페이지 분할) */}
      {pageCount > 1 && (
        <div className="mt-3 flex items-center justify-center gap-1">
          <button
            type="button"
            onClick={() => goPage(page - 1)}
            disabled={page === 0}
            className="rounded-r-1 border border-line-soft bg-surface-base px-2.5 py-1 lf-body-sm text-fg-2 hover:bg-surface-soft disabled:opacity-40"
          >
            이전
          </button>
          {Array.from({ length: pageCount }).map((_, i) => {
            // 현재 페이지 주변 + 처음/끝만 노출 (…로 축약)
            const near = Math.abs(i - page) <= 2
            const edge = i === 0 || i === pageCount - 1
            if (!near && !edge) {
              return i === page - 3 || i === page + 3 ? (
                <span key={i} className="px-1 lf-caption text-fg-3">…</span>
              ) : null
            }
            return (
              <button
                key={i}
                type="button"
                onClick={() => goPage(i)}
                className={`min-w-[32px] rounded-r-1 border px-2 py-1 lf-body-sm ${
                  i === page
                    ? 'border-brand-pink bg-brand-pink text-brand-brown'
                    : 'border-line-soft bg-surface-base text-fg-2 hover:bg-surface-soft'
                }`}
              >
                {i + 1}
              </button>
            )
          })}
          <button
            type="button"
            onClick={() => goPage(page + 1)}
            disabled={page >= pageCount - 1}
            className="rounded-r-1 border border-line-soft bg-surface-base px-2.5 py-1 lf-body-sm text-fg-2 hover:bg-surface-soft disabled:opacity-40"
          >
            다음
          </button>
        </div>
      )}

      <CreatorDetailSheet
        open={detailOpen}
        onOpenChange={setDetailOpen}
        creator={detailCreator}
        onAddToCalculator={(c) => { setDetailOpen(false); addToCart([c]) }}
      />

      <EstimateCalculatorModal open={calcOpen} onOpenChange={setCalcOpen} cart={cart} />

      <BulkActionBar
        count={selectedIds.size}
        onAddToCalculator={() => addToCart(allItems.filter((c) => selectedIds.has(c.salesforceCreatorId)))}
        onClear={() => setSelectedIds(new Set())}
      />
    </div>
  )
}
